<script lang="ts">
  // Rule Book sub-panel per INTERFACE-SPECIFICATION.md §10.2.
  //
  // Read-only view of data/dimensions/* with the validation rules
  // that derive from each table. Tabs: pricing models, bureaus,
  // band tables, housing, channels, grade-subgrade.

  import { dimensions } from '../../lib/dimensions';

  type Tab = 'pricing-models' | 'bureaus' | 'bands' | 'housing' | 'channels' | 'grades';
  let tab = $state<Tab>('pricing-models');
</script>

<section>
  <nav class="rulebook-tabs">
    {#each [
      { key: 'pricing-models', label: 'Pricing models' },
      { key: 'bureaus', label: 'Bureaus' },
      { key: 'bands', label: 'Band tables' },
      { key: 'housing', label: 'Housing' },
      { key: 'channels', label: 'Channels' },
      { key: 'grades', label: 'Grade-subgrade' }
    ] as t (t.key)}
      <button type="button" class:is-active={tab === t.key} onclick={() => (tab = t.key as Tab)}>{t.label}</button>
    {/each}
  </nav>

  {#if tab === 'pricing-models'}
    <h3 class="section-heading">Pricing models</h3>
    <table class="cube-table">
      <thead><tr><th>ID</th><th>Axes</th><th>Used by</th></tr></thead>
      <tbody>
        {#each dimensions.pricingModels as m (m.id)}
          <tr>
            <td><code>{m.id}</code></td>
            <td class="muted"><code>{m.axes.join(' × ')}</code></td>
            <td class="muted">{(m.used_by_jurisdiction_products ?? []).join(', ')}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else if tab === 'bureaus'}
    <h3 class="section-heading">Bureaus</h3>
    <table class="cube-table">
      <thead><tr><th>ID</th><th>Name</th><th>Country</th><th class="num">Score scale</th></tr></thead>
      <tbody>
        {#each dimensions.bureaus as b (b.id)}
          <tr>
            <td><code>{b.id}</code></td>
            <td>{b.name}</td>
            <td class="muted">{b.country}</td>
            <td class="num">{b.score_scale_min} – {b.score_scale_max}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else if tab === 'bands'}
    <h3 class="section-heading">Bureau-band tables</h3>
    {#each dimensions.bureauBandTables as table (table.id)}
      <div class="rule-block">
        <h4><code>{table.id}</code> · bureau <code>{table.bureau_ref}</code></h4>
        {#if table._bureau_pending_confirmation}
          <p class="status-warn">⚠ Bureau identity pending risk-team confirmation.</p>
        {/if}
        <table class="cube-table">
          <thead><tr><th>ID</th><th>Label</th><th class="num">Min</th><th class="num">Max</th><th>Public band</th></tr></thead>
          <tbody>
            {#each table.bands as band (band.id)}
              <tr>
                <td><code>{band.id}</code></td>
                <td>{band.label}</td>
                <td class="num">{band.min_score ?? '—'}</td>
                <td class="num">{band.max_score ?? '—'}</td>
                <td class="muted">{band._public_band}</td>
              </tr>
            {/each}
          </tbody>
        </table>
        <p class="muted">Validation rules: contiguous, every band mapped to a public band, rates monotonic-increasing as bands worsen.</p>
      </div>
    {/each}
  {:else if tab === 'housing'}
    <h3 class="section-heading">Housing statuses</h3>
    {#each dimensions.housingStatuses as table (table.id)}
      <table class="cube-table">
        <thead><tr><th>ID</th><th>Label</th><th class="num">Risk ordinal</th></tr></thead>
        <tbody>
          {#each table.values as v (v.id)}
            <tr><td><code>{v.id}</code></td><td>{v.label}</td><td class="num">{v._risk_ordinal}</td></tr>
          {/each}
        </tbody>
      </table>
      <p class="muted">Validation rule: rates monotonic-non-decreasing as risk ordinal grows.</p>
    {/each}
  {:else if tab === 'channels'}
    <h3 class="section-heading">Channels</h3>
    {#each dimensions.channels as table (table.id)}
      <table class="cube-table">
        <thead><tr><th>ID</th><th>Label</th><th>Default marketing</th><th class="num">Risk premium min</th><th class="num">Loan max override</th></tr></thead>
        <tbody>
          {#each table.values as c (c.id)}
            <tr>
              <td><code>{c.id}</code></td>
              <td>{c.label}</td>
              <td class="muted">{c.is_default_marketing ? '✓' : '—'}</td>
              <td class="num">{c._risk_premium_min ?? '—'}</td>
              <td class="num">{c._loan_amount_max_override ?? '—'}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/each}
  {:else if tab === 'grades'}
    <h3 class="section-heading">Grade-subgrade tables</h3>
    {#each dimensions.gradeSubgradeTables as table (table.id)}
      <table class="cube-table">
        <thead><tr><th>Grade</th><th>Subgrades</th><th>Public band</th><th class="num">Risk ordinal</th></tr></thead>
        <tbody>
          {#each table.grades as row (row.grade)}
            <tr>
              <td><code>{row.grade}</code></td>
              <td class="muted">{row.subgrades.join(', ')}</td>
              <td class="muted">{row._public_band}</td>
              <td class="num">{row._risk_ordinal}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/each}
  {/if}
</section>

<style>
  .rulebook-tabs { display: flex; gap: var(--space-2); margin-bottom: var(--space-3); flex-wrap: wrap; }
  .rulebook-tabs button {
    border: 0; background: transparent;
    padding: var(--space-1) var(--space-3);
    font-size: var(--type-sm); color: var(--surface-muted);
    border-bottom: 2px solid transparent; cursor: pointer;
  }
  .rulebook-tabs button.is-active { color: var(--surface-fg); border-bottom-color: var(--surface-fg); font-weight: 500; }
  .rule-block { margin-bottom: var(--space-4); }
  code { font-family: var(--font-mono); font-size: 0.9em; }
</style>
